import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  MessageSquare, Send, Loader2, Plus, Trash2, ArrowLeft,
  Sparkles, BookOpen, Lightbulb, DollarSign, Compass,
  Lock, GraduationCap, Clock, Copy, Check, X, FileDown
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_ACTIONS = [
  { label: 'Explain this lesson', icon: BookOpen, prompt: 'Can you explain this lesson in simpler terms? Break it down step by step.' },
  { label: 'Give me examples', icon: Lightbulb, prompt: 'Give me 3 real-world examples of how I can apply what I learned in this lesson.' },
  { label: 'Help me make money with this', icon: DollarSign, prompt: 'How can I use this skill to make money? Give me specific, actionable ideas I can start with today.' },
  { label: 'What should I do next?', icon: Compass, prompt: 'Based on what I\'ve learned, what should I do next? Give me a clear action plan.' },
];

// Course-specific quick actions when user navigates from a specific lesson/course
const COURSE_STARTERS = {
  'chatgpt-mastery': [
    { label: 'Write my first ChatGPT prompt', icon: MessageSquare, prompt: 'Help me write my first effective ChatGPT prompt for my business. Walk me through it step by step.' },
    { label: 'ChatGPT for my business', icon: DollarSign, prompt: 'What are the top 5 ways I can use ChatGPT to grow my business right now? Be specific and actionable.' },
  ],
  'midjourney-creative-mastery': [
    { label: 'My first Midjourney prompt', icon: MessageSquare, prompt: 'Help me write my first Midjourney prompt to create a professional image. What style and parameters should I use?' },
    { label: 'Sell AI art online', icon: DollarSign, prompt: 'How can I create and sell AI-generated art using Midjourney? Give me a step-by-step business plan.' },
  ],
  'claude-ai-complete-guide': [
    { label: 'Claude vs ChatGPT for me', icon: MessageSquare, prompt: 'When should I use Claude instead of ChatGPT? Help me understand the practical differences for my work.' },
    { label: 'Long documents with Claude', icon: BookOpen, prompt: 'How do I use Claude to analyze and summarize long documents? Show me the best approach.' },
  ],
  'veo-video-creation': [
    { label: 'Create my first AI video', icon: MessageSquare, prompt: 'Walk me through creating my first AI-generated video with Veo. What should I start with?' },
    { label: 'Video content for social media', icon: DollarSign, prompt: 'How can I use AI video creation to build a profitable social media presence? Give me a content plan.' },
  ],
  'prompt-engineering-beginners': [
    { label: 'Prompt template for anything', icon: MessageSquare, prompt: 'Give me a universal prompt template that works for any AI tool. Break down each part so I understand it.' },
    { label: 'Advanced prompt techniques', icon: Lightbulb, prompt: 'What are the most powerful prompt engineering techniques that most beginners don\'t know about?' },
  ],
  'ai-making-money-online': [
    { label: 'Start earning with AI today', icon: DollarSign, prompt: 'What\'s the fastest way to start making money with AI skills this week? Give me a concrete plan.' },
    { label: 'AI freelancing roadmap', icon: Compass, prompt: 'Create a 30-day roadmap for me to start freelancing with AI skills. Include what to learn and where to find clients.' },
  ],
};

// ===== COPY CODE BLOCK =====
const CodeBlock = ({ children }) => {
  const [copied, setCopied] = useState(false);
  const text = String(children).replace(/\n$/, '');
  return (
    <div className="relative my-3 group">
      <pre className="bg-slate-900 text-slate-100 rounded-lg p-3 pr-20 overflow-x-auto whitespace-pre-wrap break-words text-sm">
        <code>{text}</code>
      </pre>
      <button
        onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
        className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium flex items-center gap-1 bg-slate-700 hover:bg-primary text-slate-200 hover:text-white transition-all"
      >
        {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
      </button>
    </div>
  );
};

// ===== MESSAGE BUBBLE =====
const MessageBubble = ({ role, content, onDownloadPdf, prevMessage }) => {
  const isUser = role === 'user';
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await onDownloadPdf(content, prevMessage);
    } catch {
      // handled by parent
    }
    setDownloading(false);
  };

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4 group`} data-testid={`msg-${role}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center flex-shrink-0 mr-2 mt-1">
          <Sparkles className="w-4 h-4 text-white" />
        </div>
      )}
      <div className="flex flex-col max-w-[80%]" style={{overflowWrap: 'break-word', wordBreak: 'break-word'}}>
        <div className={`rounded-2xl px-4 py-3 ${
          isUser
            ? 'bg-primary text-white rounded-br-sm'
            : 'bg-white border border-slate-200 text-slate-800 rounded-bl-sm shadow-sm'
        }`}>
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap">{content}</p>
          ) : (
            <div className="prose prose-sm prose-slate max-w-none [&_p]:my-1 [&_ul]:my-1 [&_ol]:my-1 [&_li]:my-0.5 [&_h1]:text-base [&_h2]:text-sm [&_h3]:text-sm [&_h1]:font-bold [&_h2]:font-semibold [&_h3]:font-medium [&>*]:overflow-hidden" style={{overflowWrap: 'break-word', wordBreak: 'break-word'}}>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  pre({ children }) { return <>{children}</>; },
                  code({ inline, children }) {
                    if (inline) return <code className="bg-slate-100 px-1 rounded text-xs">{children}</code>;
                    return <CodeBlock>{children}</CodeBlock>;
                  }
                }}
              >{content}</ReactMarkdown>
            </div>
          )}
        </div>
        {!isUser && onDownloadPdf && (
          <button
            onClick={handleDownload}
            disabled={downloading}
            className="self-start mt-1.5 ml-1 flex items-center gap-1.5 text-xs text-slate-400 hover:text-primary transition-colors"
            data-testid="download-answer-pdf"
            title="Download as PDF"
          >
            {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
            <span>Save as PDF</span>
          </button>
        )}
      </div>
    </div>
  );
};

// ===== TYPING INDICATOR =====
const TypingIndicator = () => (
  <div className="flex justify-start mb-4">
    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center flex-shrink-0 mr-2 mt-1">
      <Sparkles className="w-4 h-4 text-white" />
    </div>
    <div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
      <div className="flex items-center gap-1.5">
        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
        <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  </div>
);

// ===== LOCKED VIEW =====
const LockedView = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <div className="pt-24 pb-16 flex items-center justify-center px-4">
        <Card className="max-w-md w-full p-8 text-center" data-testid="coach-locked">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-slate-400" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">AISkillFlow AI Coach</h2>
          <p className="text-slate-600 mb-1">Your personal AI learning assistant</p>
          <p className="text-sm text-slate-500 mb-6">
            Get instant answers, explanations, examples, and actionable advice on any lesson in the academy.
          </p>
          <div className="space-y-2 text-left mb-6">
            {['Ask questions about any lesson', 'Get step-by-step explanations', 'Copy-paste prompts for your work', 'Business & money-making guidance'].map((f) => (
              <div key={f} className="flex items-center gap-2 text-sm text-slate-700">
                <Check className="w-4 h-4 text-primary flex-shrink-0" />
                <span>{f}</span>
              </div>
            ))}
          </div>
          <Button onClick={() => navigate('/academy/membership')} className="w-full" data-testid="upgrade-btn">
            <GraduationCap className="w-4 h-4 mr-2" /> Unlock with Membership
          </Button>
        </Card>
      </div>
    </div>
  );
};

// ===== SESSION SIDEBAR =====
const SessionSidebar = ({ sessions, activeId, onSelect, onNew, onDelete, show, onClose }) => (
  <div className={`${show ? 'fixed inset-0 z-50 lg:relative lg:inset-auto' : 'hidden lg:block'} lg:w-72 flex-shrink-0`}>
    {show && <div className="fixed inset-0 bg-black/40 lg:hidden" onClick={onClose} />}
    <div className={`${show ? 'fixed left-0 top-0 bottom-0 w-72 z-50' : ''} bg-white border-r border-slate-200 h-full flex flex-col`}
      data-testid="session-sidebar">
      <div className="p-3 border-b border-slate-100 flex items-center justify-between">
        <h3 className="font-semibold text-sm text-slate-700">Conversations</h3>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" onClick={onNew} data-testid="new-chat-btn" title="New chat">
            <Plus className="w-4 h-4" />
          </Button>
          {show && <Button size="sm" variant="ghost" onClick={onClose} className="lg:hidden"><X className="w-4 h-4" /></Button>}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {sessions.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-4">No conversations yet</p>
        ) : sessions.map((s) => (
          <div
            key={s.id}
            className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors text-sm ${
              activeId === s.id ? 'bg-primary/10 text-primary' : 'text-slate-600 hover:bg-slate-50'
            }`}
            onClick={() => { onSelect(s.id); onClose?.(); }}
            data-testid={`session-${s.id}`}
          >
            <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
            <span className="flex-1 truncate">{s.title}</span>
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
              className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-red-50"
            >
              <Trash2 className="w-3 h-3 text-red-400 hover:text-red-600" />
            </button>
          </div>
        ))}
      </div>
    </div>
  </div>
);


// ===== MAIN COMPONENT =====
const AICoach = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, loading: authLoading } = useAuth();
  const [hasAccess, setHasAccess] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [lessonContext, setLessonContext] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const lessonId = searchParams.get('lesson');
  const courseSlug = searchParams.get('course');

  // Check access
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    checkAccess();
  }, [user, authLoading]);

  const checkAccess = async () => {
    try {
      const res = await axios.get(`${API}/academy/coach/access`, { headers });
      setHasAccess(res.data.has_access);
      if (res.data.has_access) {
        loadSessions();
        if (lessonId) loadLessonInfo();
      }
    } catch {
      setHasAccess(false);
    }
  };

  const loadLessonInfo = async () => {
    try {
      const res = await axios.get(`${API}/academy/lesson/${lessonId}`, { headers });
      setLessonContext({ title: res.data.title, course: res.data.course?.title });
    } catch {}
  };

  const loadSessions = async () => {
    try {
      const res = await axios.get(`${API}/academy/coach/sessions`, { headers });
      setSessions(res.data);
    } catch {}
  };

  const loadSession = async (sessionId) => {
    try {
      const res = await axios.get(`${API}/academy/coach/sessions/${sessionId}`, { headers });
      setMessages(res.data.messages || []);
      setActiveSessionId(sessionId);
    } catch { toast.error('Failed to load conversation'); }
  };

  const startNewChat = () => {
    setActiveSessionId(null);
    setMessages([]);
    inputRef.current?.focus();
  };

  const deleteSession = async (sessionId) => {
    try {
      await axios.delete(`${API}/academy/coach/sessions/${sessionId}`, { headers });
      setSessions((prev) => prev.filter((s) => s.id !== sessionId));
      if (activeSessionId === sessionId) startNewChat();
      toast.success('Conversation deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const sendMessage = useCallback(async (text) => {
    if (!text?.trim() || sending) return;
    const msg = text.trim();
    setInput('');
    setSending(true);

    // Optimistic UI
    setMessages((prev) => [...prev, { role: 'user', content: msg }]);

    try {
      const res = await axios.post(`${API}/academy/coach/chat`, {
        message: msg,
        session_id: activeSessionId,
        lesson_id: lessonId || undefined,
        course_slug: courseSlug || undefined,
      }, { headers });

      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.response }]);

      if (!activeSessionId) {
        setActiveSessionId(res.data.session_id);
      }
      loadSessions();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to get response';
      toast.error(detail);
      // Remove the optimistic user message on error
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setSending(false);
    }
  }, [sending, activeSessionId, lessonId, courseSlug, headers]);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const downloadAnswerPdf = async (content, question) => {
    try {
      const res = await axios.post(`${API}/academy/coach/answer/pdf`, {
        content,
        question: question || null,
      }, {
        headers: { ...headers, 'Accept': 'application/pdf' },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'ai_coach_answer.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch {
      toast.error('Failed to generate PDF');
    }
  };

  if (hasAccess === null) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="pt-24 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  if (!hasAccess) return <LockedView />;

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col" data-testid="ai-coach-page">
      <Navbar />

      <div className="pt-16 flex-1 flex overflow-hidden">
        {/* Sidebar */}
        <SessionSidebar
          sessions={sessions}
          activeId={activeSessionId}
          onSelect={loadSession}
          onNew={startNewChat}
          onDelete={deleteSession}
          show={showSidebar}
          onClose={() => setShowSidebar(false)}
        />

        {/* Chat Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
            <Button size="sm" variant="ghost" className="lg:hidden" onClick={() => setShowSidebar(true)} data-testid="toggle-sidebar">
              <MessageSquare className="w-4 h-4" />
            </Button>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-slate-900 text-sm">AISkillFlow AI Coach</h2>
              {lessonContext ? (
                <p className="text-xs text-slate-500 truncate">
                  Helping with: {lessonContext.title} ({lessonContext.course})
                </p>
              ) : (
                <p className="text-xs text-slate-500">Your personal AI learning assistant</p>
              )}
            </div>
            {lessonId && (
              <Button size="sm" variant="outline" onClick={() => navigate(`/academy/lesson/${lessonId}`)}>
                <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Back to Lesson
              </Button>
            )}
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-6">
            <div className="max-w-3xl mx-auto">
              {messages.length === 0 && !sending && (
                <div className="text-center py-12" data-testid="coach-welcome">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-primary/20">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2">
                    {lessonContext ? `Let's master "${lessonContext.title}"` : "Hey! I'm your AI Coach"}
                  </h2>
                  <p className="text-slate-600 mb-8 max-w-md mx-auto">
                    {lessonContext
                      ? "Ask me anything about this lesson — I'll explain it in simple terms with practical examples."
                      : "Ask me anything about AI tools, prompt engineering, or how to grow your business with AI."}
                  </p>

                  {/* Quick Actions - dynamic based on course context */}
                  {(() => {
                    const courseStarters = courseSlug && COURSE_STARTERS[courseSlug] ? COURSE_STARTERS[courseSlug] : [];
                    const actions = [...courseStarters, ...QUICK_ACTIONS].slice(0, 4);
                    return (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg mx-auto">
                        {actions.map(({ label, icon: Icon, prompt }) => (
                          <button
                            key={label}
                            onClick={() => sendMessage(prompt)}
                            className="flex items-center gap-3 px-4 py-3 bg-white border border-slate-200 rounded-xl hover:border-primary hover:shadow-md transition-all text-left group"
                            data-testid={`quick-${label.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                              <Icon className="w-4.5 h-4.5 text-primary" />
                            </div>
                            <span className="text-sm font-medium text-slate-700 group-hover:text-primary transition-colors">{label}</span>
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              )}

              {messages.map((msg, i) => (
                <MessageBubble
                  key={i}
                  role={msg.role}
                  content={msg.content}
                  onDownloadPdf={msg.role === 'assistant' ? downloadAnswerPdf : undefined}
                  prevMessage={msg.role === 'assistant' && i > 0 ? messages[i - 1]?.content : undefined}
                />
              ))}

              {sending && <TypingIndicator />}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Input */}
          <div className="bg-white border-t border-slate-200 px-4 py-3">
            <div className="max-w-3xl mx-auto flex items-end gap-2">
              <div className="flex-1 relative">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={lessonContext ? `Ask about "${lessonContext.title}"...` : "Ask your AI Coach anything..."}
                  className="pr-12 py-3 text-sm rounded-xl border-slate-200 focus:border-primary"
                  disabled={sending}
                  data-testid="coach-input"
                />
              </div>
              <Button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || sending}
                className="rounded-xl h-10 px-4"
                data-testid="coach-send-btn"
              >
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-center text-xs text-slate-400 mt-2">
              AI Coach uses course content to give you personalized guidance
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AICoach;
